import React, { useState, useEffect } from 'react';
    import { Link, useNavigate, useLocation } from 'react-router-dom';
    import { useAuth } from '../contexts/AuthContext';
    import { useApp } from '../contexts/AppContext';
    import { motion } from 'framer-motion';
    import SafeIcon from '../common/SafeIcon';
    import * as FiIcons from 'react-icons/fi';
    import { Circle, Square, Triangle } from './GeometricShapes';

    const {
      FiBell,
      FiUser,
      FiLogOut,
      FiSettings,
      FiSun,
      FiMoon,
    } = FiIcons;

    const Navbar = () => {
      const { user, logout } = useAuth();
      const {
        notifications,
        theme,
        updateAppearance,
        addNotification,
        toggleNotificationCenter,
        setShowNotificationCenter,
      } = useApp();
      const navigate = useNavigate();
      const location = useLocation();
      const [showProfileMenu, setShowProfileMenu] = useState(false);

      // Close menus when route changes
      useEffect(() => {
        setShowProfileMenu(false);
        setShowNotificationCenter(false);
      }, [location, setShowNotificationCenter]);

      // Close menus when clicking outside
      useEffect(() => {
        const handleClickOutside = (event) => {
          if (!event.target.closest('.dropdown-container')) {
            setShowProfileMenu(false);
          }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
      }, []);

      const handleLogout = () => {
        logout();
        navigate('/login');
      };

      const handleBellClick = () => {
        toggleNotificationCenter();
        setShowProfileMenu(false);
      };

      const toggleTheme = () => {
        const newTheme = theme === 'dark' ? 'light' : 'dark';
        updateAppearance({ theme: newTheme });
        addNotification({
          type: 'success',
          title: 'Theme Updated',
          message: `Switched to ${newTheme} mode`,
        });
      };

      // Logo animation variants
      const logoVariants = {
        initial: { scale: 0.8, opacity: 0, y: -10 },
        animate: {
          scale: 1,
          opacity: 1,
          y: 0,
          transition: { type: 'spring', stiffness: 260, damping: 20, duration: 0.6 },
        },
        hover: { scale: 1.05, transition: { type: 'spring', stiffness: 400, damping: 10 } },
        tap: { scale: 0.95 },
      };

      return (
        <nav className="mobile-top-nav bg-white/95 dark:bg-neutral-900/95 backdrop-blur-md border-b border-neutral-200 dark:border-neutral-800">
          <div className="flex items-center justify-between h-16 px-4">
            {/* Left Section - Logo with Geometric Animation */}
            <div className="flex items-center flex-shrink-0">
              <Link to="/" className="flex items-center">
                <motion.div
                  initial="initial"
                  animate="animate"
                  whileHover="hover"
                  whileTap="tap"
                  variants={logoVariants}
                  className="overflow-visible relative"
                >
                  {/* Geometric frame around logo */}
                  <div className="absolute -inset-2 opacity-20">
                    <div className="absolute top-0 left-0">
                      <Triangle size="sm" color="blue" rotation={15} />
                    </div>
                    <div className="absolute bottom-0 right-0">
                      <Circle size="sm" color="green" />
                    </div>
                  </div>
                  <img
                    src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1752685004146-blob"
                    alt="ILM Global Logo"
                    className="h-10 w-auto relative z-10 rounded-lg shadow-geometric"
                  />
                </motion.div>
              </Link>
            </div>

            {/* Center Section - Elfsight Translator */}
            <div className="flex-1 flex justify-center items-center">
              <div className="elfsight-app-2fd27e10-eb04-46b4-81e7-ee368a81dc44" data-elfsight-app-lazy></div>
            </div>

            {/* Right Section with Geometric Elements */}
            <div className="flex items-center space-x-2 flex-shrink-0">
              {/* Theme Toggle with Geometric Design */}
              <button
                onClick={toggleTheme}
                className="p-2 text-neutral-600 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors touch-target rounded-xl hover:bg-neutral-100 dark:hover:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 hover:border-primary-300 dark:hover:border-primary-500 shadow-geometric hover:shadow-geometric-lg relative overflow-hidden group"
                aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                <div className="absolute inset-0 opacity-5 group-hover:opacity-10 transition-opacity">
                  <Square size="sm" color="primary" rotation={45} />
                </div>
                <SafeIcon icon={theme === 'dark' ? FiSun : FiMoon} className="w-5 h-5 relative z-10" />
              </button>

              {/* Notifications with Geometric Badge */}
              <div className="relative dropdown-container">
                <button
                  onClick={handleBellClick}
                  className="relative p-2 text-neutral-600 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors touch-target rounded-xl hover:bg-neutral-100 dark:hover:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 hover:border-primary-300 dark:hover:border-primary-500 shadow-geometric hover:shadow-geometric-lg overflow-hidden group"
                  aria-label="Notifications"
                >
                  <div className="absolute inset-0 opacity-5 group-hover:opacity-10 transition-opacity">
                    <Circle size="sm" color="primary" />
                  </div>
                  <SafeIcon icon={FiBell} className="w-5 h-5 relative z-10" />
                  {notifications.length > 0 && (
                    <span className="absolute -top-1 -right-1 bg-accent-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center font-medium animate-geometric-pulse border-2 border-white dark:border-neutral-800 shadow-geometric">
                      {notifications.length > 9 ? '9+' : notifications.length}
                    </span>
                  )}
                </button>
              </div>

              {/* User Profile with Geometric Frame */}
              <div className="relative dropdown-container">
                <button
                  onClick={() => setShowProfileMenu(!showProfileMenu)}
                  className="flex items-center space-x-2 p-1 rounded-xl hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors touch-target border border-neutral-200 dark:border-neutral-700 hover:border-primary-300 dark:hover:border-primary-500 shadow-geometric hover:shadow-geometric-lg group"
                  aria-expanded={showProfileMenu}
                  aria-haspopup="true"
                >
                  <div className="relative">
                    <img
                      src={user?.avatar}
                      alt={user?.name}
                      className="w-8 h-8 rounded-lg object-cover border-2 border-neutral-200 dark:border-neutral-700 group-hover:border-primary-300 dark:group-hover:border-primary-500 flex-shrink-0 transition-colors"
                      loading="lazy"
                    />
                    {/* Geometric status indicator */}
                    <div className="absolute -bottom-1 -right-1">
                      <Circle size="sm" color="green" filled className="text-green-500 w-3 h-3" />
                    </div>
                  </div>
                </button>
                {showProfileMenu && (
                  <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-neutral-800 rounded-2xl shadow-geometric-xl border-2 border-neutral-200 dark:border-neutral-700 py-2 z-50 backdrop-blur-sm overflow-hidden">
                    {/* Geometric decoration */}
                    <div className="absolute top-2 right-2 opacity-5">
                      <Triangle size="md" color="primary" />
                    </div>
                    <div className="px-4 py-2 border-b border-neutral-100 dark:border-neutral-700 relative z-10">
                      <p className="text-sm font-medium text-neutral-800 dark:text-neutral-200 text-ellipsis">{user?.name}</p>
                      <p className="text-xs text-neutral-500 dark:text-neutral-400 text-ellipsis">{user?.points} points</p>
                    </div>
                    <Link
                      to="/profile"
                      className="flex items-center space-x-2 px-4 py-2 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors relative z-10"
                      onClick={() => setShowProfileMenu(false)}
                    >
                      <SafeIcon icon={FiUser} className="w-4 h-4 flex-shrink-0" />
                      <span className="text-ellipsis">Profile</span>
                    </Link>
                    <Link
                      to="/settings"
                      className="flex items-center space-x-2 px-4 py-2 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors relative z-10"
                      onClick={() => setShowProfileMenu(false)}
                    >
                      <SafeIcon icon={FiSettings} className="w-4 h-4 flex-shrink-0" />
                      <span className="text-ellipsis">Settings</span>
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="flex items-center space-x-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/50 w-full text-left transition-colors relative z-10"
                    >
                      <SafeIcon icon={FiLogOut} className="w-4 h-4 flex-shrink-0" />
                      <span className="text-ellipsis">Logout</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </nav>
      );
    };

    export default Navbar;